'use client';

import { c as _c } from "react/compiler-runtime";
import type { ReactNode } from 'react';
import type { LucideIcon } from 'lucide-react';
import { GitCompare, HeartHandshake, LayoutGrid } from 'lucide-react';
import { useLabsSurface } from '../labs-surface';
import { useLabsStore, usePack } from '../pack-context';
import { PillTag, StepNav } from './_components';
import { StepShell } from './step-shell';

/**
 * M2 · Intro — the framing slide for the mindset quiz, in the SOURCE vanilla prototype's `.view-intro`
 * layout (`data-view="m2-intro"` + `.intro-points` in styles.css), rendered through the Extern
 * finance-sprint design system (fs-* tokens + DM Serif Display / Inter).
 *
 * Layout: the same centered framing slide as m1-intro — eyebrow pill, serif headline, a muted lead
 * line ("What you'd do is one signal. How you think is the other." in finance), then a scannable
 * LEFT-ALIGNED `.intro-points` list (NOT centered wrapping chips): each point is a brand-green leading
 * icon + a medium-weight line. The shared top-left "← Back" is owned by StepNav, so the body carries
 * only a primary Continue.
 *
 * COPY vs ICONS: the three bullets are pack-owned (`copy.m2.intro.points` — a vertical names its own
 * situations), but the icons are ENGINE chrome: they illustrate the *shape* of the promise (a set of
 * situations · two reads on each · no wrong answers), which is identical for every vertical. So they
 * stay here and are zipped with the copy by index — a pack cannot ship a lucide import, and asking it
 * to name an icon would put a rendering concern in the content layer.
 *
 * Token mapping (SOURCE intent → fs-* token):
 *   - source `.intro-point i` brand-green #0F4D3A  → `fs-success` (the green token)
 *   - source `.intro-point span` ink #0A0E1A 500   → `fs-text` + font-medium
 *   - source `.intro-body` #3A4252                 → `fs-text-subtle`
 */

/** Icons for the SOURCE `.intro-point` items, in copy order (layout-grid / git-compare / heart-handshake). */
const POINT_ICONS: readonly LucideIcon[] = [LayoutGrid, GitCompare, HeartHandshake];
export function M2Intro() {
  const $ = _c(20);
  const {
    copy
  } = usePack();
  const next = useLabsStore(_temp);
  const {
    moduleCount
  } = useLabsSurface();
  let t0;
  if ($[0] !== moduleCount) {
    t0 = <PillTag>Module 2 of {moduleCount}</PillTag>;
    $[0] = moduleCount;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let t1;
  if ($[2] !== copy.m2.intro.headline) {
    t1 = <h1 className="font-instrument text-fs-display-sm text-fs-text sm:text-fs-display-md">{copy.m2.intro.headline}</h1>;
    $[2] = copy.m2.intro.headline;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  let t2;
  if ($[4] !== copy.m2.intro.lead) {
    t2 = <p className="font-medium text-fs-text-subtle text-fs-title-lg tracking-[-1px]">{copy.m2.intro.lead}</p>;
    $[4] = copy.m2.intro.lead;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  if ($[6] !== t0 || $[7] !== t1 || $[8] !== t2) {
    t3 = <div className="flex w-full max-w-[560px] flex-col items-center gap-4 text-center">{t0}{t1}{t2}</div>;
    $[6] = t0;
    $[7] = t1;
    $[8] = t2;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  let t4;
  if ($[10] !== copy.m2.intro.points) {
    t4 = copy.m2.intro.points.map(_temp2);
    $[10] = copy.m2.intro.points;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  let t5;
  if ($[12] !== t4) {
    t5 = <ul className="flex w-full max-w-[30rem] flex-col gap-2.5 text-left">{t4}</ul>;
    $[12] = t4;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  let t6;
  if ($[14] !== next) {
    t6 = <StepNav onNext={next} />;
    $[14] = next;
    $[15] = t6;
  } else {
    t6 = $[15];
  }
  let t7;
  if ($[16] !== t3 || $[17] !== t5 || $[18] !== t6) {
    t7 = <StepShell><div className="flex flex-col items-center gap-8">{t3}{t5}{t6}</div></StepShell>;
    $[16] = t3;
    $[17] = t5;
    $[18] = t6;
    $[19] = t7;
  } else {
    t7 = $[19];
  }
  return t7;
}
function _temp2(point, i) {
  const Icon = POINT_ICONS[i];
  return <li className="flex items-start gap-2.5" key={point}>{Icon ? <Icon className="mt-0.5 h-[18px] w-[18px] shrink-0 text-fs-success" strokeWidth={2} /> : null}<span className="font-medium text-fs-body-lg text-fs-text">{point}</span></li>;
}
function _temp(s) {
  return s.next;
}